import { apiClient } from '@/api/client';
import {
  SETUP_WIZARD_COMPANY_SETUP,
  SETUP_WIZARD_COMPANY_STATUSES,
  SETUP_WIZARD_DEMO_DATA,
  SETUP_WIZARD_DRAFTS,
  SETUP_WIZARD_IMPORTS,
  SETUP_WIZARD_IMPORT_HISTORY,
  SETUP_WIZARD_IMPORT_TEMPLATE,
  SETUP_WIZARD_STATUS,
  setupWizardDraftImportCheckPath,
  setupWizardDraftImportSetAsidePath,
  setupWizardDraftImportsPath,
  setupWizardDraftLaunchPath,
  setupWizardDraftPath,
  setupWizardImportCommitPath,
  setupWizardImportMappingPath,
  setupWizardImportPath,
  setupWizardImportSetAsidePath,
  setupWizardImportValidatePath,
} from '@/api/endpoints';
import type { ApiResponseWrapper } from '@/types/api.types';
import type {
  CompanySetupPayload,
  CompanySetupResult,
  CompanySetupStatus,
  ImportHistoryPage,
  ImportJob,
  ImportMappingPayload,
  SetupDraft,
  SetupDraftLaunchResult,
  SetupDraftPayload,
  SetupWizardStatus,
} from '@/types/setup-wizard.types';

function unwrap<T>(value: ApiResponseWrapper<T> | T): T {
  const wrapped = value as ApiResponseWrapper<T>;
  return wrapped?.data !== undefined ? wrapped.data : (value as T);
}

export async function getSetupWizardStatus(
  companyId?: string
): Promise<SetupWizardStatus | CompanySetupStatus> {
  const { data } = await apiClient.get<
    | ApiResponseWrapper<SetupWizardStatus | CompanySetupStatus>
    | SetupWizardStatus
    | CompanySetupStatus
  >(SETUP_WIZARD_STATUS, { params: companyId ? { companyId } : undefined });
  return unwrap(data);
}

export async function listCompanySetupStatuses(): Promise<CompanySetupStatus[]> {
  const { data } = await apiClient.get<
    ApiResponseWrapper<CompanySetupStatus[]> | CompanySetupStatus[]
  >(SETUP_WIZARD_COMPANY_STATUSES);
  return unwrap(data);
}

export async function saveCompanySetup(payload: CompanySetupPayload): Promise<CompanySetupResult> {
  const { data } = await apiClient.put<
    ApiResponseWrapper<CompanySetupResult> | CompanySetupResult
  >(SETUP_WIZARD_COMPANY_SETUP, payload);
  return unwrap(data);
}

export async function createSetupDraft(payload: SetupDraftPayload = {}): Promise<SetupDraft> {
  const { data } = await apiClient.post<ApiResponseWrapper<SetupDraft> | SetupDraft>(
    SETUP_WIZARD_DRAFTS,
    payload
  );
  return unwrap(data);
}

export async function listSetupDrafts(): Promise<SetupDraft[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<SetupDraft[]> | SetupDraft[]>(
    SETUP_WIZARD_DRAFTS
  );
  return unwrap(data);
}

export async function getSetupDraft(id: string): Promise<SetupDraft> {
  const { data } = await apiClient.get<ApiResponseWrapper<SetupDraft> | SetupDraft>(
    setupWizardDraftPath(id)
  );
  return unwrap(data);
}

export async function updateSetupDraft(
  id: string,
  payload: SetupDraftPayload
): Promise<SetupDraft> {
  const { data } = await apiClient.patch<ApiResponseWrapper<SetupDraft> | SetupDraft>(
    setupWizardDraftPath(id),
    payload
  );
  return unwrap(data);
}

export async function discardSetupDraft(id: string): Promise<{ deleted: boolean }> {
  const { data } = await apiClient.delete<ApiResponseWrapper<{ deleted: boolean }> | { deleted: boolean }>(
    setupWizardDraftPath(id)
  );
  return unwrap(data);
}

export async function uploadSetupDraftImport(id: string, file: File): Promise<SetupDraft> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<ApiResponseWrapper<SetupDraft> | SetupDraft>(
    setupWizardDraftImportsPath(id),
    formData
  );
  return unwrap(data);
}

export async function checkSetupDraftImportRows(
  id: string,
  importId: string,
  payload: ImportMappingPayload
): Promise<SetupDraft> {
  const { data } = await apiClient.post<ApiResponseWrapper<SetupDraft> | SetupDraft>(
    setupWizardDraftImportCheckPath(id, importId),
    payload
  );
  return unwrap(data);
}

export async function launchSetupDraft(id: string): Promise<SetupDraftLaunchResult> {
  const { data } = await apiClient.post<
    ApiResponseWrapper<SetupDraftLaunchResult> | SetupDraftLaunchResult
  >(setupWizardDraftLaunchPath(id));
  return unwrap(data);
}

export async function downloadEmployeeImportTemplate(
  companyId?: string,
  draftId?: string,
  companyCode?: string
): Promise<Blob> {
  const { data } = await apiClient.get(SETUP_WIZARD_IMPORT_TEMPLATE, {
    params: {
      type: 'employees',
      ...(companyId ? { companyId } : {}),
      ...(draftId ? { draftId } : {}),
      ...(companyCode ? { companyCode } : {}),
    },
    responseType: 'blob',
  });
  return data;
}

export async function createImportDraft(file: File, companyId?: string): Promise<ImportJob> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', 'employees');
  if (companyId) formData.append('companyId', companyId);
  const { data } = await apiClient.post<ApiResponseWrapper<ImportJob> | ImportJob>(
    SETUP_WIZARD_IMPORTS,
    formData
  );
  return unwrap(data);
}

export async function getImportJob(id: string): Promise<ImportJob> {
  const { data } = await apiClient.get<ApiResponseWrapper<ImportJob> | ImportJob>(
    setupWizardImportPath(id)
  );
  return unwrap(data);
}

export async function listImportJobs(companyId?: string): Promise<ImportJob[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<ImportJob[]> | ImportJob[]>(
    SETUP_WIZARD_IMPORTS,
    { params: companyId ? { companyId } : undefined }
  );
  return unwrap(data);
}

export async function listImportHistory(page: number, limit: number): Promise<ImportHistoryPage> {
  const { data } = await apiClient.get<ApiResponseWrapper<ImportHistoryPage> | ImportHistoryPage>(
    SETUP_WIZARD_IMPORT_HISTORY,
    { params: { page, limit } }
  );
  return unwrap(data);
}

export async function saveImportMapping(
  id: string,
  payload: ImportMappingPayload
): Promise<ImportJob> {
  const { data } = await apiClient.put<ApiResponseWrapper<ImportJob> | ImportJob>(
    setupWizardImportMappingPath(id),
    payload
  );
  return unwrap(data);
}

export async function validateImportJob(id: string): Promise<{ jobId: string }> {
  const { data } = await apiClient.post<ApiResponseWrapper<{ jobId: string }> | { jobId: string }>(
    setupWizardImportValidatePath(id)
  );
  return unwrap(data);
}

export async function commitImportJob(id: string): Promise<{ jobId: string }> {
  const { data } = await apiClient.post<ApiResponseWrapper<{ jobId: string }> | { jobId: string }>(
    setupWizardImportCommitPath(id)
  );
  return unwrap(data);
}

/**
 * Sets aside the rows with problems (`set-aside-failed`) or puts them back
 * (`restore-set-aside`). A company import returns the job, whose remaining
 * rows are checked again in the background; a setup draft returns the draft,
 * already re-checked.
 */
export async function changeImportSetAside(
  target: { importId: string; draftId?: string | null },
  action: 'set-aside-failed' | 'restore-set-aside'
): Promise<ImportJob | SetupDraft> {
  const path = target.draftId
    ? setupWizardDraftImportSetAsidePath(target.draftId, target.importId, action)
    : setupWizardImportSetAsidePath(target.importId, action);
  const { data } = await apiClient.post<
    ApiResponseWrapper<ImportJob | SetupDraft> | ImportJob | SetupDraft
  >(path);
  return unwrap(data);
}

/** The set-aside rows as a CSV with their problems, to fix and import again. */
export async function downloadImportSetAsideCsv(target: {
  importId: string;
  draftId?: string | null;
}): Promise<Blob> {
  const path = target.draftId
    ? setupWizardDraftImportSetAsidePath(target.draftId, target.importId, 'set-aside.csv')
    : setupWizardImportSetAsidePath(target.importId, 'set-aside.csv');
  const { data } = await apiClient.get(path, { responseType: 'blob' });
  return data;
}

export async function loadSetupDemoData(force = false): Promise<unknown> {
  const { data } = await apiClient.post(SETUP_WIZARD_DEMO_DATA, { force });
  return data;
}
